
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../integrations/supabase/client";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { getUserSubscriptionInfo, checkRemainingQuota } from "../services/subscriptionService";
import { usePaymentPlatform } from "../contexts/PaymentPlatformContext";

interface SubscriptionData {
  subscription_tier: 'free' | 'standard' | 'premium' | 'ultimate';
  paypal_subscription_id?: string;
  stripe_customer_id?: string;
  payment_provider: string;
  subscription_source?: string;
}

interface EnhancedSubscriptionData {
  currentTier: 'free' | 'standard' | 'premium' | 'ultimate';
  subscription_tier: 'free' | 'standard' | 'premium' | 'ultimate';
  paypal_subscription_id?: string;
  stripe_customer_id?: string;
  payment_provider: string;
  subscription_source?: string;
}

export function useSubscription() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isNative, nativeTier } = usePaymentPlatform();
  
  const subscriptionQuery = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('Fetching subscription for user:', user.id);
      
      const { data, error } = await supabase
        .from('users')
        .select('subscription_tier, paypal_subscription_id, stripe_customer_id, payment_provider, subscription_source')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching subscription:', error);
        throw new Error(`Error fetching subscription: ${error.message}`);
      }

      if (!data) {
        throw new Error('No subscription data found');
      }

      console.log('Subscription data:', data);
      return data as SubscriptionData;
    },
    enabled: !!user?.id,
    retry: 3,
    retryDelay: 1000,
  });

  const quotaQuery = useQuery({
    queryKey: ['quota', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      return await checkRemainingQuota();
    },
    enabled: !!user?.id,
    retry: 2,
    retryDelay: 1000,
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: async ({ tier, subscriptionId, provider }: { tier: 'free' | 'standard' | 'premium' | 'ultimate'; subscriptionId?: string; provider?: string }) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const updateData: any = { subscription_tier: tier };
      
      if (provider) {
        updateData.payment_provider = provider;
      }
      
      if (subscriptionId && provider === 'paypal') {
        updateData.paypal_subscription_id = subscriptionId;
      }

      const { data, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        throw new Error(`Error updating subscription: ${error.message}`);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['quota'] });
      toast.success("Subscription updated successfully!");
    },
    onError: (error: Error) => {
      console.error("Subscription update error:", error);
      toast.error(`Failed to update subscription: ${error.message}`);
    }
  });

  // Transform the subscription data to include currentTier for backward compatibility
  // On native platforms, use nativeTier from RevenueCat as source of truth (webhook may be delayed)
  const effectiveTier = (isNative && nativeTier) ? nativeTier : subscriptionQuery.data?.subscription_tier;
  
  const enhancedSubscription: { data: EnhancedSubscriptionData } | undefined = 
    subscriptionQuery.data ? {
      data: {
        ...subscriptionQuery.data,
        currentTier: effectiveTier || subscriptionQuery.data.subscription_tier,
        subscription_tier: effectiveTier || subscriptionQuery.data.subscription_tier
      }
    } : (isNative && nativeTier && nativeTier !== 'free') ? {
      data: {
        currentTier: nativeTier,
        subscription_tier: nativeTier,
        payment_provider: 'revenuecat',
        subscription_source: isNative ? 'google' : 'apple',
      }
    } : undefined;

  const isAnyLoading = subscriptionQuery.isLoading || quotaQuery.isLoading;

  return {
    subscription: enhancedSubscription,
    quota: quotaQuery.data,
    isLoading: subscriptionQuery.isLoading,
    isAnyLoading,
    error: subscriptionQuery.error,
    updateSubscription: updateSubscriptionMutation.mutate,
    isUpdating: updateSubscriptionMutation.isPending,
  };
}
